import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Wallet } from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LoadingState } from "@/components/ui/loading-state";
import { Button } from "@/components/ui/button";
import { PeriodPicker, PeriodPreset, periodFor } from "@/components/PeriodPicker";
import { useOrganizationWallet } from "@/hooks/useSubscription";
import { KpiPeriod } from "@/models/org-kpis";
import { useOrgKpis } from "./hooks/useOrgKpis";
import { MessagesTab } from "./components/MessagesTab";
import { CostTab } from "./components/CostTab";
import { DeliveryTab } from "./components/DeliveryTab";
import { NodesTab } from "./components/NodesTab";
import { HandlingTab } from "./components/HandlingTab";
import { CampaignsTab } from "./components/CampaignsTab";
import { ActivityTab } from "./components/ActivityTab";
import { PlanCard } from "./components/PlanCard";

const OrganizationDetailPage = () => {
  const { organizationId = "" } = useParams<{ organizationId: string }>();
  const [preset, setPreset] = useState<PeriodPreset>("last_30_days");
  const [period, setPeriod] = useState<KpiPeriod>(() => periodFor("last_30_days"));
  const { data: kpis, isLoading, error, refetch } = useOrgKpis(organizationId, period);
  const { data: wallet } = useOrganizationWallet(organizationId);

  const handlePeriod = (next: PeriodPreset, nextPeriod: KpiPeriod) => {
    setPreset(next);
    setPeriod(nextPeriod);
  };

  return (
    <div className="space-y-6">
      <Button asChild variant="ghost" size="sm" className="-ml-2 gap-1 text-muted-foreground">
        <Link to="/organizations">
          <ArrowLeft className="h-4 w-4" /> Organizations
        </Link>
      </Button>

      <PageHeader
        title={kpis?.organization.name ?? "Organization"}
        description={kpis?.organization.orgId ? `ID: ${kpis.organization.orgId}` : undefined}
        className="mb-0"
        action={
          wallet ? (
            <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
              <Wallet className="h-4 w-4 text-muted-foreground" />
              Wallet
              <span className="font-semibold tabular-nums">
                {(wallet.balance / 100).toFixed(2)} SAR
              </span>
            </div>
          ) : undefined
        }
      />

      <PlanCard organizationId={organizationId} />

      <PeriodPicker preset={preset} period={period} onChange={handlePeriod} />

      {isLoading || error || !kpis ? (
        <LoadingState
          isLoading={isLoading}
          error={error}
          loadingText="Loading usage..."
          errorTitle="Failed to load usage"
          onRetry={() => refetch()}
        />
      ) : (
        <Tabs defaultValue="messages" className="space-y-6">
          <TabsList className="flex h-auto flex-wrap justify-start">
            <TabsTrigger value="messages">Messages</TabsTrigger>
            <TabsTrigger value="cost">Meta cost</TabsTrigger>
            <TabsTrigger value="nodes">Bot nodes</TabsTrigger>
            <TabsTrigger value="delivery">Delivery</TabsTrigger>
            <TabsTrigger value="handling">Bot vs human</TabsTrigger>
            <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
          </TabsList>
          <TabsContent value="messages">
            <MessagesTab messages={kpis.messages} />
          </TabsContent>
          <TabsContent value="cost">
            <CostTab cost={kpis.cost} />
          </TabsContent>
          <TabsContent value="nodes">
            <NodesTab organizationId={organizationId} period={period} />
          </TabsContent>
          <TabsContent value="delivery">
            <DeliveryTab delivery={kpis.delivery} />
          </TabsContent>
          <TabsContent value="handling">
            <HandlingTab organizationId={organizationId} period={period} />
          </TabsContent>
          <TabsContent value="campaigns">
            <CampaignsTab organizationId={organizationId} period={period} />
          </TabsContent>
          <TabsContent value="activity">
            <ActivityTab organizationId={organizationId} period={period} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default OrganizationDetailPage;
